// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import PersonLine from "@store/Library/Components/Toolkit/Icons/Person/PersonLine";
import TextSeparator, { TextSeparatorPresentationProps } from "@store/Library/Components/Toolkit/TextSeparator";
import { signInProps } from "@store/Themes/common/props/signIn";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { useIsNamedContext } from "@toolkit/Framework/Components/NamedContext";
import parse from "html-react-parser";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    fullWidthButton = "fullWidthButton",
    showIcon = "showIcon",
    title = "title",
    text = "text",
}

interface OwnProps extends WidgetProps, HasShellContext {
    fields: {
        [fields.fullWidthButton]: boolean;
        [fields.showIcon]: boolean;
        [fields.title]: string;
        [fields.text]: string;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const returnUrl = getReturnUrl(state);

    return {
        allowGuestCheckout:
            getSettingsCollection(state).accountSettings.allowGuestCheckout && !getCurrentUserIsGuest(state),
        showGuestCheckout:
            returnUrl === getPageLinkByPageType(state, "CheckoutShippingPage")?.url || props.shellContext.isEditing,
        returnUrl,
    };
};

const mapDispatchToProps = {
    signInAsGuest,
};

export interface SignInAsGuestStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    content?: InjectableCss;
    button?: ButtonPresentationProps;
    icon?: IconPresentationProps;
    separator?: TextSeparatorPresentationProps;
}

export const signInAsGuestStyles: SignInAsGuestStyles = {
    wrapper: signInProps.wrapper,
    icon: {
        ...signInProps.icon,
        src: PersonLine,
    },
    title: signInProps.title,
    button: { variant: "secondary" },
    separator: signInProps.separator,
};

const SignInAsGuestComponent: FC<Props> = ({
    fields: { fullWidthButton, showIcon, title, text },
    allowGuestCheckout,
    returnUrl,
    signInAsGuest,
    showGuestCheckout,
}) => {
    const isSignInOverlay = useIsNamedContext(constants.pageType.signInOverlay);
    const styles = React.useMemo(() => {
        if (!isSignInOverlay) {
            return signInAsGuestStyles;
        }

        return mergeProps<SignInAsGuestStyles>(signInAsGuestStyles, {
            wrapper: signInProps.overlayOverrides?.wrapper,
            icon: signInProps?.overlayOverrides?.icon,
            title: signInProps?.overlayOverrides?.title,
        });
    }, [isSignInOverlay]);

    if (!allowGuestCheckout && !showGuestCheckout) {
        return null;
    }

    const handleCheckoutAsGuest = () => {
        if (!returnUrl) {
            return;
        }

        signInAsGuest({ returnUrl });
    };

    return (
        <>
            {isSignInOverlay && <TextSeparator {...styles.separator} />}
            <StyledWrapper {...styles.wrapper}>
                {showIcon && <Icon {...styles.icon} />}
                {title && <Typography {...styles.title}>{title}</Typography>}
                {text && <StyledWrapper {...styles.content}>{parse(text, parserOptions)}</StyledWrapper>}
                <FormActions fullWidth={fullWidthButton}>
                    <Button
                        {...styles.button}
                        onClick={handleCheckoutAsGuest}
                        data-test-selector="signInAsGuest_checkoutAsGuest"
                    >
                        {translate("Checkout as Guest")}
                    </Button>
                </FormActions>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withIsInShell(connect(mapStateToProps, mapDispatchToProps)(SignInAsGuestComponent)),
    definition: {
        allowedContexts: ["SignInPage", "SignInOverlay"],
        group: "Sign In",
        icon: "user-unlock",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Checkout as Guest",
                fieldType: "Translatable",
            },
            {
                name: fields.text,
                displayName: "Content",
                editorTemplate: "RichTextField",
                defaultValue: `<p>You don't need to be registered to proceed to checkout. However, it is recommended for faster ordering as well as access to time and money-saving advantages.</p>\n\n<ul>\n<li>Order without creating an account</li>\n<li>Track your Orders</li>\n</ul>`,
                fieldType: "Translatable",
            },
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
